import { FORMAT_META } from './config.js';
import type { HandResult } from './scoring.js';
import type { CardId, PlayerId, Rank, SessionFormat } from './types.js';

/**
 * One played-out hand, kept for the hand-by-hand breakdown (§4.5).
 *
 * `results` carries each player's *arrangement* — the groups they were scored
 * on. `reveal` carries what they were actually holding, which is not the same
 * thing and cannot be recovered from the other: a player who dropped has no
 * arrangement at all, and a player who mis-grouped has one that does not
 * account for every card. Both are needed to show a finished hand back to the
 * table.
 */
export interface SessionHandRecord {
  ordinal: number;
  results: HandResult[];
  /**
   * Final holdings, by player. Optional because records written before hands
   * were kept simply do not have it — an old session rehydrates without cards
   * rather than failing to rehydrate.
   */
  reveal?: Record<PlayerId, CardId[]>;
  /**
   * What was wild in *this* hand, and the rules it was dealt under.
   *
   * Recorded per hand rather than read off the live table, because the cut card
   * changes every deal: replaying hand 1's cards against hand 4's wild rank
   * silently redraws which cards were jokers and which melds were pure, and the
   * result looks authoritative while being wrong.
   */
  wildCard?: CardId;
  wildRank?: Rank | null;
  configId?: string;
}

export interface SessionPlayerState {
  score: number;
  eliminated: boolean;
  /** Pool 201 permits one re-entry at the current highest score (§3.8). */
  reentries: number;
  eliminatedAtHand: number | null;
  /**
   * The re-entry, kept so the table can explain a score that moved backwards.
   * Crossing 201 and resuming at the field's highest is the rule working, but
   * a player who sees 198 become 190 with nothing said has been given a bug.
   *
   * Optional: records written before this was kept simply have no note.
   */
  reentry?: { hand: number; from: number; to: number } | null;
}

export interface SessionState {
  format: SessionFormat;
  /** Seat order, fixed at session start. */
  players: PlayerId[];
  standings: Record<PlayerId, SessionPlayerState>;
  handsPlayed: number;
  complete: boolean;
  winners: PlayerId[];
  /** Every hand's results, for the hand-by-hand breakdown (§4.5). */
  history: SessionHandRecord[];
  /** Room-level toggle; only meaningful for pool-201. */
  reentryEnabled: boolean;
}

export function createSession(
  format: SessionFormat,
  players: readonly PlayerId[],
  options: { reentryEnabled?: boolean } = {},
): SessionState {
  const standings: Record<PlayerId, SessionPlayerState> = {};
  for (const p of players) {
    standings[p] = {
      score: 0,
      eliminated: false,
      reentries: 0,
      eliminatedAtHand: null,
      reentry: null,
    };
  }
  return {
    format,
    players: [...players],
    standings,
    handsPlayed: 0,
    complete: false,
    winners: [],
    history: [],
    reentryEnabled: options.reentryEnabled ?? FORMAT_META[format].reentry,
  };
}

/** Seats that take part in the next hand. */
export function activeSeats(session: SessionState): PlayerId[] {
  return session.players.filter((p) => !session.standings[p]!.eliminated);
}

/** What the hand looked like, for the record kept of it. */
export interface HandRecordDetail {
  reveal?: Readonly<Record<PlayerId, readonly CardId[]>>;
  wildCard?: CardId;
  wildRank?: Rank | null;
  configId?: string;
}

export function applyHandResults(
  session: SessionState,
  ordinal: number,
  results: readonly HandResult[],
  detail?: HandRecordDetail,
): SessionState {
  const meta = FORMAT_META[session.format];
  const standings: Record<PlayerId, SessionPlayerState> = {};
  for (const [p, s] of Object.entries(session.standings)) standings[p] = { ...s };

  for (const r of results) {
    const seat = standings[r.player];
    if (!seat || seat.eliminated) continue;
    seat.score += r.points;
  }

  const handsPlayed = session.handsPlayed + 1;

  if (meta.kind === 'pool') {
    const threshold = meta.threshold!;
    for (const [p, seat] of Object.entries(standings)) {
      if (seat.eliminated || seat.score < threshold) continue;
      const alive = Object.values(standings).filter((s) => !s.eliminated).length;
      const highest = Math.max(
        ...Object.entries(standings)
          .filter(([id, s]) => !s.eliminated && id !== p && s.score < threshold)
          .map(([, s]) => s.score),
        0,
      );
      // Re-entry is only offered while there is still a session to re-enter.
      if (session.reentryEnabled && seat.reentries === 0 && alive > 2) {
        seat.reentries = 1;
        seat.reentry = { hand: handsPlayed, from: seat.score, to: highest };
        seat.score = highest;
      } else {
        seat.eliminated = true;
        seat.eliminatedAtHand = handsPlayed;
      }
    }
  }

  const record: SessionHandRecord = { ordinal, results: [...results] };
  if (detail?.reveal) {
    const held: Record<PlayerId, CardId[]> = {};
    for (const [p, cards] of Object.entries(detail.reveal)) held[p] = [...cards];
    record.reveal = held;
  }
  if (detail?.wildCard !== undefined) record.wildCard = detail.wildCard;
  if (detail?.wildRank !== undefined) record.wildRank = detail.wildRank;
  if (detail?.configId !== undefined) record.configId = detail.configId;

  const next: SessionState = {
    ...session,
    standings,
    handsPlayed,
    history: [...session.history, record],
  };

  const alive = activeSeats(next);
  if (meta.kind === 'deals') {
    if (handsPlayed >= meta.deals!) {
      next.complete = true;
      next.winners = lowestScorers(next);
    }
  } else if (alive.length <= 1) {
    next.complete = true;
    next.winners = alive.length === 1 ? [alive[0]!] : lowestScorers(next);
  }

  return next;
}

/**
 * Takes seats out of the running session without touching their scores — a
 * player who walked away or was dropped for being absent (§8.3) keeps every
 * point they conceded and stops being dealt in.
 *
 * Separate from `applyHandResults` on purpose: the caller decides *when*, and
 * that has to be after the hand that removed them has been scored.
 */
export function eliminate(
  session: SessionState,
  players: readonly PlayerId[],
): SessionState {
  const targets = players.filter((p) => session.standings[p] && !session.standings[p]!.eliminated);
  if (targets.length === 0) return session;

  const standings: Record<PlayerId, SessionPlayerState> = {};
  for (const [p, s] of Object.entries(session.standings)) standings[p] = { ...s };
  for (const p of targets) {
    standings[p]!.eliminated = true;
    standings[p]!.eliminatedAtHand = session.handsPlayed;
  }

  const next: SessionState = { ...session, standings };
  if (next.complete) return next;
  const alive = activeSeats(next);
  if (alive.length <= 1) {
    next.complete = true;
    next.winners = alive.length === 1 ? [alive[0]!] : lowestScorers(next);
  }
  return next;
}

/**
 * Lowest score wins. A seat that is out of the session is not in the running for
 * it — otherwise walking away on a good score would be a winning move — so the
 * survivors are ranked first and everyone is only considered when nobody is left.
 */
function lowestScorers(session: SessionState): PlayerId[] {
  const alive = session.players.filter((p) => !session.standings[p]!.eliminated);
  const contenders = alive.length > 0 ? alive : session.players;
  const entries = contenders.map((p) => [p, session.standings[p]!.score] as const);
  const min = Math.min(...entries.map(([, s]) => s));
  return entries.filter(([, s]) => s === min).map(([p]) => p);
}

export function sessionLabel(format: SessionFormat): string {
  const meta = FORMAT_META[format];
  return meta.kind === 'deals' ? `deals-${meta.deals}` : `pool-${meta.threshold}`;
}

/** Total hands in a deals session, or `null` for pool (which ends on standings). */
export function totalDeals(format: SessionFormat): number | null {
  return FORMAT_META[format].deals ?? null;
}
